// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/growth-score/pages/AssessmentPage.tsx (ADAPT)
// ADAPTED (plan P4-1, P4-6): reads our view model. The cohort is the session's confirmed competitors (CI `similarity` as the
// match), with their own logos; TP's "inspiration" brands and the Omnichannel owner badge are not ported (no source, no
// place to keep an owner). The calibration button opens the read-only cohort page; "Your brand profile" (NEW) opens the
// brand page, which TP reached from the chat's side panel.
/**
 * Growth Score → Your assessment: the overall score against the cohort
 * median, the five dimensions on a radar, every dimension as a row that opens
 * its page, and the cohort the score is ranked against. Revealed in sequence
 * on open: the card lands, the score counts up and the radar grows, the
 * verdict fades in, then the rows stagger in with their bars.
 */
import { ArrowRight, ChevronRight } from 'lucide-react'
import { SCORE_REFERENCE } from '@/contracts/onboarding-session'
import { Badge } from '@/components/ui/shadcn/badge'
import { Button } from '@/components/ui/shadcn/button'
import { Card } from '@/components/ui/shadcn/card'
import { headline } from '../growth-score-model'
import { GrowthPage, GrowthSection, useGrowthNav } from '../GrowthPage'
import { useGrowthScore } from '../GrowthScoreContext'
import { DimensionRadar } from '../components/charts'
import { BrandFaces, DimensionIcon, ScoreBar, ScoreFigure } from '../components/visuals'

/** Most brand marks in the cohort strip. */
const MAX_FACES = 12

export function AssessmentPage() {
  const vm = useGrowthScore()
  const nav = useGrowthNav()
  const { dimensions, overall, worst } = vm
  const scored = dimensions.filter((d) => d.weight > 0 && d.score != null)
  const faces = vm.competitors.slice(0, MAX_FACES)
  const names = faces.map((b) => b.name)
  // The weights are the BFF's, per dimension: nothing here assumes them.
  const weighting = scored.length ? `How it weighs: ${scored.map((d) => `${d.short} ${d.weight}%`).join(', ')}.` : ''

  return (
    <GrowthPage title="Your assessment" hideTitle>
      <Card className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] overflow-hidden animate-in fade-in zoom-in-95 duration-500 motion-reduce:animate-none">
        <div className="flex min-w-0 flex-col gap-3 p-4">
          <span className="text-caption text-ds-text-2">{vm.brand} score</span>
          <ScoreFigure value={overall} size="xl" reveal delay={300} />
          <div className="flex flex-col gap-1.5">
            <ScoreBar value={overall} size="lg" reveal delay={300} />
            <div className="flex justify-between text-label text-ds-text-2">
              <span>You {overall}</span>
              <span>Cohort median {SCORE_REFERENCE}</span>
            </div>
          </div>
          <p className="text-heading-sm text-foreground animate-in fade-in slide-in-from-bottom-1 fill-mode-both duration-500 delay-1000 motion-reduce:animate-none">{headline(worst)}</p>
          <p className="text-label text-ds-text-2 animate-in fade-in fill-mode-both duration-500 delay-1000 motion-reduce:animate-none">
            {weighting} Checks with no score yet don&apos;t count.
          </p>
        </div>
        <div className="flex min-w-0 flex-col gap-1 border-l border-ds-hairline p-4">
          <div className="flex items-baseline justify-between">
            <h3 className="text-heading-sm text-foreground">By dimension</h3>
            <span className="text-label text-ds-text-2">{SCORE_REFERENCE} = cohort median</span>
          </div>
          {scored.length ? (
            <DimensionRadar dimensions={scored.map((d) => ({ key: d.key, label: d.short, score: d.score! }))} reveal delay={400} />
          ) : (
            <p className="py-6 text-body-sm text-ds-text-2">No dimension has a score yet.</p>
          )}
        </div>
      </Card>

      <GrowthSection title="Where you have the most to gain" sub="By dimension, heaviest first. Open one to see every check.">
        <Card className="divide-y divide-ds-hairline overflow-hidden">
          {dimensions.map((d, i) => (
            <button
              key={d.key}
              type="button"
              data-opener={`dimension:${d.key}`}
              onClick={() => nav.open({ kind: 'dimension', dim: d.key })}
              style={{ animationDelay: `${900 + i * 120}ms` }}
              className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors animate-in fade-in slide-in-from-bottom-2 fill-mode-both duration-500 hover:bg-ds-ink-a05 focus-visible:bg-ds-ink-a05 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ds-focus motion-reduce:animate-none"
            >
              <DimensionIcon dimension={d.key} />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-heading-sm text-foreground">{d.name}</span>
                  <span className="text-label text-ds-text-3">{d.weight ? `${d.weight}%` : 'not scored'}</span>
                </div>
                <p className="truncate text-body-sm text-ds-text-2" title={`${d.gap.lead} ${d.gap.rest}`}>
                  <span className="text-tab text-foreground">{d.gap.lead}</span> {d.gap.rest}
                </p>
              </div>
              <div className="flex w-32 shrink-0 flex-col items-end gap-1.5">
                {d.score == null ? (
                  <Badge intent="neutral">Signal</Badge>
                ) : (
                  <>
                    <ScoreFigure value={d.score} reveal delay={1000 + i * 120} />
                    <ScoreBar value={d.score} reveal delay={1000 + i * 120} />
                  </>
                )}
              </div>
              <ChevronRight aria-hidden className="size-4 shrink-0 text-ds-text-3" />
            </button>
          ))}
        </Card>
      </GrowthSection>

      <Card className="flex flex-wrap items-center gap-4 p-5 animate-in fade-in slide-in-from-bottom-2 fill-mode-both duration-500 delay-[1600ms] motion-reduce:animate-none">
        {faces.length ? <BrandFaces brands={faces} label={`Your cohort: ${names.join(', ')}`} /> : null}
        <p className="min-w-56 flex-1 text-body-sm text-ds-text-2">
          {vm.competitors.length
            ? `Your comparable cohort: ${vm.competitors.length} ${vm.competitors.length === 1 ? 'competitor' : 'competitors'}.`
            : vm.competitorsSkipped
              ? "You didn't pick competitors, so no named brands are behind this score."
              : "Your cohort will show here once it's ready."}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" data-opener="brand" onClick={() => nav.open({ kind: 'brand' })}>
            Your brand profile
          </Button>
          <Button type="button" data-opener="cohort" onClick={() => nav.open({ kind: 'cohort' })}>
            See your comparable cohort
            <ArrowRight aria-hidden />
          </Button>
        </div>
      </Card>
    </GrowthPage>
  )
}
